/* Levara's curved TOC path, adapted to an isolated Shopify article controller. */
(() => {
  if (customElements.get('mw-article')) return;
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const number = n => Number(n.toFixed(2));
  function pathFor(points) {
    if (!points.length) return '';
    let path = `M ${points[0].x} 0 L ${points[0].x} ${number(points[0].y)}`;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1], b = points[i], dx = b.x - a.x, dy = b.y - a.y;
      if (!dx || dy <= 0) { path += ` L ${number(b.x)} ${number(b.y)}`; continue; }
      const sign = Math.sign(dx), turn = a.y + dy / 2;
      const rx = Math.min(Math.abs(dx) / 2, 10), ry = Math.min(dy / 2, 10), k = .5522847498;
      const firstX = a.x + sign * rx, secondX = b.x - sign * rx;
      path += ` L ${number(a.x)} ${number(turn - ry)} C ${number(a.x)} ${number(turn - ry * k)}, ${number(firstX - sign * rx * k)} ${number(turn)}, ${number(firstX)} ${number(turn)}`;
      if (Math.abs(firstX - secondX) > .5) path += ` L ${number(secondX)} ${number(turn)}`;
      path += ` C ${number(secondX + sign * rx * k)} ${number(turn)}, ${number(b.x)} ${number(turn + ry - ry * k)}, ${number(b.x)} ${number(turn + ry)} L ${number(b.x)} ${number(b.y)}`;
    }
    return path;
  }
  class MwArticle extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.sectionNode?.removeEventListener('shopify:section:load', this.onLoad);
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.body = this.querySelector('[data-mw-body]');
      this.desktop = matchMedia('(min-width: 1280px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.toc = this.querySelector('[data-mw-toc]');
      this.items = [];
      if (this.body) {
        this.wrapTables(); this.readingTime(); this.buildToc();
        this.observer = new ResizeObserver(() => this.schedule(true));
        this.observer.observe(this.body);
        const list = this.querySelector('[data-mw-toc-list]');
        if (list) this.observer.observe(list);
      }
      this.desktopOpen ??= this.toc?.dataset.desktopOpen !== 'false';
      this.mobileOpen ??= this.toc?.dataset.mobileOpen === 'true';
      this.syncExpansion();
      this.toc?.addEventListener('toggle', () => {
        // A breakpoint update sets .open before its queued toggle event fires.
        // Save the state for the breakpoint currently shown, never force reopening.
        if (this.desktop.matches) this.desktopOpen = this.toc.open;
        else this.mobileOpen = this.toc.open;
        this.schedule(true);
      }, { signal });
      this.desktop.addEventListener('change', () => { this.syncExpansion(); this.schedule(true); }, { signal });
      window.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      window.addEventListener('resize', () => this.schedule(true), { passive: true, signal });
      document.addEventListener('eurus:sticky-header-moved', event => {
        const height = Number(event.detail?.heightHeader);
        if (Number.isFinite(height)) { this.headerHeight = Math.max(0, height); this.schedule(true); }
      }, { signal });
      this.addEventListener('click', event => this.click(event), { signal });
      this.setupTooltips(signal);
      // Custom-element connection handles editor replacement, and these events also
      // support editors that keep the DOM node while unloading/reloading its section.
      this.sectionNode = this.closest('.shopify-section') || this;
      this.onLoad = () => { this.connectedCallback(); this.schedule(true); };
      this.onUnload = () => this.cleanup(false);
      this.sectionNode.addEventListener('shopify:section:load', this.onLoad);
      this.sectionNode.addEventListener('shopify:section:unload', this.onUnload, { signal });
      this.querySelectorAll('[data-mw-copy]').forEach(button => { button.hidden = false; });
      document.fonts?.ready.then(() => { if (!signal.aborted) this.schedule(true); });
      this.schedule(true);
    }
    disconnectedCallback() { this.cleanup(true); }
    cleanup(removeLoad) {
      this.hideTooltip();
      clearTimeout(this.tooltipTimer);
      this.tooltip?.remove(); this.tooltip = null;
      this.querySelectorAll('[data-mw-tooltip]').forEach(button => {
        button.setAttribute('title', button.dataset.mwTooltip);
      });
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame); this.frame = null;
      if (removeLoad) this.sectionNode?.removeEventListener('shopify:section:load', this.onLoad);
    }
    wrapTables() {
      this.body.querySelectorAll('table').forEach((table, index) => {
        if (table.parentElement.classList.contains('mw-article-table')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'mw-article-table'; wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || `Article table ${index + 1}`);
        table.before(wrapper); wrapper.append(table);
      });
    }
    readingTime() {
      const clone = this.body.cloneNode(true);
      clone.querySelectorAll('script, style, template').forEach(node => node.remove());
      // Boundaries prevent adjacent paragraphs/cells from becoming a single word.
      clone.querySelectorAll('p, div, li, br, h1, h2, h3, h4, h5, h6, td, th, blockquote').forEach(node => node.append(' '));
      const text = clone.textContent.trim(), count = text ? text.split(/\s+/u).length : 0;
      this.querySelectorAll('[data-mw-read-time]').forEach(label => {
        const wpm = clamp(Number(label.dataset.wpm) || 200, 100, 400);
        label.textContent = `${Math.max(1, Math.ceil(count / wpm))} min read`;
        label.hidden = count === 0;
      });
    }
    buildToc() {
      if (!this.toc) return;
      this.items = [];
      const depth = clamp(Number(this.toc.dataset.depth) || 3, 2, 4);
      const list = this.toc.querySelector('[data-mw-toc-list]'); list.replaceChildren();
      const headings = [...this.body.querySelectorAll('h2, h3, h4')].filter(h => Number(h.tagName[1]) <= depth && h.textContent.trim());
      // Keep the first existing ID; suffix duplicate IDs and reserve IDs elsewhere
      // in this section as well as headings excluded by the selected depth.
      const reserved = new Set([...this.querySelectorAll('[id]')].map(node => node.id));
      const seen = new Set();
      headings.forEach((heading, index) => {
        let id = heading.id;
        if (!id || seen.has(id)) {
          const base = id || heading.textContent.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || `section-${index + 1}`;
          id = base; let suffix = 2;
          while (reserved.has(id) || document.getElementById(id)) id = `${base}-${suffix++}`;
          heading.id = id; reserved.add(id);
        }
        seen.add(id);
        const item = document.createElement('li'), link = document.createElement('a');
        item.className = 'mw-article-toc__item';
        item.style.setProperty('--mw-toc-depth', Number(heading.tagName[1]) - 2);
        link.className = 'mw-article-toc__link'; link.href = `#${encodeURIComponent(id)}`;
        link.textContent = heading.textContent.trim(); item.append(link); list.append(item);
        this.items.push({ heading, item, link });
      });
      this.toc.hidden = !this.items.length;
      const rail = this.querySelector('.mw-article-left');
      if (rail) rail.hidden = !this.items.length && !rail.querySelector('[data-mw-sidebar]');
    }
    syncExpansion() { if (this.toc) this.toc.open = this.desktop.matches ? this.desktopOpen : this.mobileOpen; }
    setupTooltips(signal) {
      const buttons = [...this.querySelectorAll('[data-mw-tooltip]')];
      if (!buttons.length) return;
      this.tooltip = document.createElement('div');
      this.tooltip.className = 'mw-article-tooltip';
      this.tooltip.id = `MwShareTooltip-${this.closest('.shopify-section')?.id || this.id || crypto.randomUUID()}`;
      this.tooltip.setAttribute('role', 'tooltip');
      this.tooltip.setAttribute('popover', 'manual');
      this.tooltip.hidden = true; this.append(this.tooltip);
      const close = () => {
        clearTimeout(this.tooltipTimer);
        // Allow the pointer to cross the gap between the control and tooltip.
        this.tooltipTimer = setTimeout(() => {
          if (!this.tooltip?.matches(':hover') && !this.tooltipButton?.matches(':hover') && document.activeElement !== this.tooltipButton) this.hideTooltip();
        }, 120);
      };
      buttons.forEach(button => {
        button.removeAttribute('title');
        button.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') this.showTooltip(button); }, { signal });
        button.addEventListener('pointerleave', close, { signal });
        button.addEventListener('focus', () => this.showTooltip(button), { signal });
        button.addEventListener('blur', close, { signal });
      });
      this.tooltip.addEventListener('pointerenter', () => clearTimeout(this.tooltipTimer), { signal });
      this.tooltip.addEventListener('pointerleave', close, { signal });
      document.addEventListener('keydown', event => { if (event.key === 'Escape') this.hideTooltip(); }, { signal });
      window.visualViewport?.addEventListener('resize', () => this.schedule(), { signal });
      window.visualViewport?.addEventListener('scroll', () => this.schedule(), { signal });
    }
    showTooltip(button) {
      if (!this.tooltip || button.hidden) return;
      clearTimeout(this.tooltipTimer);
      this.tooltipButton?.removeAttribute('aria-describedby');
      this.tooltipButton = button;
      this.tooltip.textContent = button.dataset.mwTooltip;
      button.setAttribute('aria-describedby', this.tooltip.id);
      this.tooltipPending = true;
      this.tooltip.style.visibility = 'hidden';
      this.tooltip.hidden = false;
      // The top layer prevents clipping by theme animation/overflow containers.
      if (this.tooltip.showPopover && !this.tooltip.matches(':popover-open')) this.tooltip.showPopover();
      // Keyboard focus can start the theme's smooth scrolling to an offscreen
      // control. Position after layout, then reveal once the control is visible.
      this.schedule();
    }
    hideTooltip() {
      this.tooltipButton?.removeAttribute('aria-describedby'); this.tooltipButton = null;
      this.tooltipPending = false;
      if (this.tooltip) {
        if (this.tooltip.hidePopover && this.tooltip.matches(':popover-open')) this.tooltip.hidePopover();
        this.tooltip.hidden = true;
      }
    }
    positionTooltip() {
      if (!this.tooltipButton || !this.tooltip || this.tooltip.hidden) return;
      const visual = window.visualViewport;
      const viewport = { left: visual?.offsetLeft || 0, top: visual?.offsetTop || 0, width: visual?.width || document.documentElement.clientWidth, height: visual?.height || innerHeight };
      const margin = 8, gap = 10;
      const bounds = { left: viewport.left + margin, top: viewport.top + margin, right: viewport.left + viewport.width - margin, bottom: viewport.top + viewport.height - margin };
      const anchor = this.tooltipButton.getBoundingClientRect();
      if (anchor.bottom < bounds.top || anchor.top > bounds.bottom || anchor.right < bounds.left || anchor.left > bounds.right) {
        if (!this.tooltipPending || document.activeElement !== this.tooltipButton) this.hideTooltip();
        return;
      }
      this.tooltip.style.setProperty('--mw-tooltip-width', `${Math.min(240, viewport.width - 2 * margin)}px`);
      this.tooltip.style.setProperty('--mw-tooltip-height', `${viewport.height - 2 * margin}px`);
      const { width, height } = this.tooltip.getBoundingClientRect();
      const centerX = anchor.left + anchor.width / 2, centerY = anchor.top + anchor.height / 2;
      const placements = {
        left: { x: anchor.left - width - gap, y: centerY - height / 2 },
        right: { x: anchor.right + gap, y: centerY - height / 2 },
        top: { x: centerX - width / 2, y: anchor.top - height - gap },
        bottom: { x: centerX - width / 2, y: anchor.bottom + gap }
      };
      const preference = this.desktop.matches ? ['left', 'right', 'top', 'bottom'] : ['top', 'bottom', 'left', 'right'];
      // Shift along the cross axis before flipping: an inline control near the
      // left edge can still use an above tooltip that fits inside the viewport.
      const candidates = preference.map(side => {
        const point = { side, ...placements[side] };
        if (side === 'top' || side === 'bottom') point.x = clamp(point.x, bounds.left, bounds.right - width);
        else point.y = clamp(point.y, bounds.top, bounds.bottom - height);
        return point;
      });
      const overflow = point => Math.max(0, bounds.left - point.x) + Math.max(0, point.x + width - bounds.right) + Math.max(0, bounds.top - point.y) + Math.max(0, point.y + height - bounds.bottom);
      const chosen = candidates.reduce((best, point) => overflow(point) < overflow(best) ? point : best);
      this.tooltip.dataset.position = chosen.side;
      this.tooltip.style.setProperty('--mw-tooltip-x', `${clamp(chosen.x, bounds.left, bounds.right - width)}px`);
      this.tooltip.style.setProperty('--mw-tooltip-y', `${clamp(chosen.y, bounds.top, bounds.bottom - height)}px`);
      this.tooltipPending = false;
      this.tooltip.style.visibility = 'visible';
    }
    schedule(measure = false) {
      if (!this.abort) return;
      this.needsMeasure ||= measure;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.needsMeasure) { this.needsMeasure = false; this.measure(); }
        this.progress();
        this.positionTooltip();
      });
    }
    measure() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--height-header');
      // These stable theme IDs are the only external DOM references; article
      // content, links and controls are always queried inside this element.
      const header = document.getElementById('x-header-container');
      const headerContent = document.getElementById('sticky-header-content');
      const announcement = document.getElementById('x-announcement');
      const initialHeight = header?.dataset.sticky === 'none' ? 0 : (headerContent?.offsetHeight ?? (parseFloat(raw) || 96));
      const announcementHeight = announcement?.dataset.isSticky === 'true' ? announcement.offsetHeight : 0;
      // Reserve room for an on-scroll-up header even while it is hidden: an
      // upward TOC jump can reveal it during the scroll animation.
      const headerHeight = Math.max(this.headerHeight ?? 0, parseFloat(raw) || 0, initialHeight + announcementHeight);
      this.offset = headerHeight + 24;
      this.style.setProperty('--mw-sticky-offset', `${this.offset}px`);
      if (!this.items.length || !this.desktop.matches || !this.toc.open) return;
      const list = this.toc.querySelector('[data-mw-toc-list]');
      const top = list.getBoundingClientRect().top;
      this.points = this.items.map(({ heading, item }) => ({ x: 10 + (Number(heading.tagName[1]) - 2) * 14, y: item.getBoundingClientRect().top - top + item.offsetHeight / 2 }));
      const svg = this.toc.querySelector('.mw-article-toc__svg');
      svg.setAttribute('width', list.offsetWidth); svg.setAttribute('height', list.offsetHeight);
      const path = pathFor(this.points);
      svg.querySelectorAll('[data-mw-path-bg], [data-mw-path-fg]').forEach(node => node.setAttribute('d', path));
    }
    progress() {
      if (!this.items.length) return;
      const offset = this.offset || 120, body = this.body.getBoundingClientRect();
      const positions = this.items.map(item => item.heading.getBoundingClientRect().top);
      let active = -1;
      if (body.top <= offset) {
        active = 0;
        positions.forEach((top, index) => { if (top <= offset + 1) active = index; });
      }
      this.items.forEach(({ link }, index) => { if (index === active) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current'); });
      if (!this.points?.length || !this.toc.open) return;
      let height = 0;
      if (active >= 0) {
        const start = Math.max(body.top, positions[active]), end = positions[active + 1] ?? body.bottom;
        const fraction = clamp((offset - start) / Math.max(1, end - start), 0, 1);
        height = this.points[active].y + ((this.points[active + 1]?.y ?? this.points[active].y) - this.points[active].y) * fraction;
      }
      this.toc.querySelector('[data-mw-clip]').setAttribute('height', number(height));
    }
    async click(event) {
      const link = event.target.closest('.mw-article-toc__link');
      if (link && this.contains(link) && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
        const item = this.items.find(item => item.link === link);
        if (!item) return;
        event.preventDefault(); this.measure();
        history.replaceState(history.state, '', link.hash);
        item.heading.tabIndex = -1; item.heading.focus({ preventScroll: true });
        window.scrollTo({ top: window.scrollY + item.heading.getBoundingClientRect().top - this.offset, behavior: this.reduced.matches ? 'instant' : 'smooth' });
        return;
      }
      const button = event.target.closest('[data-mw-copy]');
      if (!button || !this.contains(button)) return;
      const status = this.querySelector('[data-mw-share-status]');
      const signal = this.abort.signal;
      button.disabled = true;
      try {
        await navigator.clipboard.writeText(button.dataset.mwCopy);
        if (!signal.aborted) status.textContent = 'Link copied.';
      } catch {
        if (!signal.aborted) status.textContent = `Unable to copy. Article link: ${button.dataset.mwCopy}`;
      } finally { if (!signal.aborted) button.disabled = false; }
    }
  }
  customElements.define('mw-article', MwArticle);
})();
